import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useList, useTranslate } from "@refinedev/core";
import type { HttpError } from "@refinedev/core";
import {
  Button,
  DataTable,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  ToggleField,
  type Column,
} from "@fawkes/ui";

import { ErrorState, LoadingState } from "../../components/states";
import type { Locale } from "../../i18n/messages";
import { formatDateTime } from "../portal/format";
import type {
  AdminCredential,
  AdminOrganization,
  AdminServiceAccount,
  AllowedIp,
  IssuedCredential,
  ResetLink,
} from "./types";

// The fixed admin-extend buckets — value mirrors ServiceAccountCredentialAdminService.ExtensionDuration
// (an unknown value is a Jackson 400 before the service runs); label is i18n.
const EXTEND_DURATIONS: { value: string; labelKey: string }[] = [
  { value: "HOURS_24", labelKey: "admin.sa.detail.extend.h24" },
  { value: "HOURS_72", labelKey: "admin.sa.detail.extend.h72" },
  { value: "WEEK_1", labelKey: "admin.sa.detail.extend.w1" },
  { value: "MONTH_1", labelKey: "admin.sa.detail.extend.mo1" },
];

// jmix-9.2: Service accounts — list / create (reveal-once secret) and a dedicated detail VIEW
// (ServiceAccountDetail, a full shell page reached from the list — no longer a cramped modal). The
// detail page fetches its own account via GET /api/admin/service-accounts/{id} and refetches after each
// mutation. The minted plaintext secret is held in component state ONLY, shown once, never re-fetched
// (the L-sensitive reveal-once contract). 401/403 → logout via authProvider.onError; 409/400 do NOT
// (surfaced inline).

function statusOf(error: unknown): number | undefined {
  return (error as HttpError | undefined)?.statusCode;
}

/** The most-recent `lastUsedAt` across an account's live credentials, or null if none was ever used. */
function mostRecentLastUsed(credentials: AdminCredential[]): string | null {
  let latest: string | null = null;
  for (const c of credentials) {
    if (c.lastUsedAt && (latest === null || c.lastUsedAt > latest)) {
      latest = c.lastUsedAt;
    }
  }
  return latest;
}

type SaStatusFilter = "active" | "disabled" | "all" | "removed";

// The default scopes pre-filled into an empty mint field (operator can edit before minting): the full
// server-side catalogue (M2mScopes). A backoffice requests all of these in ONE token, so a credential missing
// any one of them (the old five-scope default lacked nazorg.invite + publications.write) fails every mint.
const DEFAULT_SCOPES =
  "foster.activate foster.deactivate foster.update absences.read absences.write nazorg.invite publications.write";

// ============================================================ list

export function ServiceAccounts({ onOpen }: { onOpen: (id: string) => void }) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const [status, setStatus] = useState<SaStatusFilter>("active");
  const { data, isLoading, isError, refetch } = useList<AdminServiceAccount>({
    resource: "admin/service-accounts",
    pagination: { mode: "off" },
    filters: status === "active" ? [] : [{ field: "status", operator: "eq", value: status }],
  });
  const { mutate, isLoading: mutating } = useCustomMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [createError, setCreateError] = useState<string | undefined>();
  // A pending restore confirmation: a human-readable message + the action to run on "yes".
  const [confirm, setConfirm] = useState<{ message: string; action: () => void } | null>(null);
  const [listActionError, setListActionError] = useState<string | undefined>();

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  const rows = data?.data ?? [];

  function openCreate() {
    setNewUsername("");
    setCreateError(undefined);
    setCreateOpen(true);
  }

  function submitCreate() {
    setCreateError(undefined);
    if (newUsername.trim().length === 0) {
      setCreateError(t("admin.sa.create.error.required"));
      return;
    }
    mutate(
      { url: "admin/service-accounts", method: "post", values: { username: newUsername.trim() } },
      {
        onSuccess: (res) => {
          setCreateOpen(false);
          void refetch();
          const created = res?.data as AdminServiceAccount | undefined;
          if (created?.id) {
            onOpen(created.id);
          }
        },
        onError: (error) => {
          const s = statusOf(error);
          setCreateError(
            s === 409 ? t("admin.sa.create.error.duplicate") : t("admin.sa.create.error.generic"),
          );
        },
      },
    );
  }

  function restoreAccount(account: AdminServiceAccount) {
    setListActionError(undefined);
    mutate(
      { url: `admin/service-accounts/${account.id}/restore`, method: "post", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setListActionError(t("admin.sa.detail.action.error")),
      },
    );
  }

  const columns: Column<AdminServiceAccount>[] = [
    { header: t("admin.sa.column.username"), cell: (a) => a.username },
    {
      header: t("admin.sa.column.status"),
      cell: (a) =>
        status === "removed" ? (
          <StatusBadge tone="warning">{t("admin.sa.status.softDeleted")}</StatusBadge>
        ) : (
          <StatusBadge tone={a.active ? "success" : "neutral"}>
            {t(a.active ? "admin.common.active" : "admin.common.inactive")}
          </StatusBadge>
        ),
    },
    {
      header: t("admin.sa.column.webPortal"),
      cell: (a) =>
        status === "removed" ? (
          <span className="fawkes-portal__muted">—</span>
        ) : (
          <StatusBadge tone={a.webPortalAccessEnabled ? "success" : "neutral"}>
            {t(a.webPortalAccessEnabled ? "admin.common.enabled" : "admin.common.disabled")}
          </StatusBadge>
        ),
    },
    {
      header: t("admin.sa.column.lastUsed"),
      cell: (a) => {
        if (status === "removed") {
          return <span className="fawkes-portal__muted">—</span>;
        }
        const last = mostRecentLastUsed(a.credentials);
        return last ? formatDateTime(last, locale) : t("admin.sa.cred.neverUsed");
      },
    },
    {
      header: t("admin.common.actions"),
      align: "right",
      // Removed accounts have no live sub-resources — the only action is restore (no detail view).
      cell: (a) =>
        status === "removed" ? (
          <Button
            variant="secondary"
            disabled={mutating}
            onClick={() =>
              setConfirm({ message: t("admin.sa.confirm.restoreAccount"), action: () => restoreAccount(a) })
            }
          >
            {t("admin.sa.detail.restore")}
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => onOpen(a.id)}>
            {t("admin.common.manage")}
          </Button>
        ),
    },
  ];

  return (
    <div className="fawkes-admin-page">
      <div className="fawkes-admin__toolbar">
        <SelectField
          label={t("admin.sa.filter.status")}
          value={status}
          onChange={(e) => {
            setListActionError(undefined);
            setStatus(e.target.value as SaStatusFilter);
          }}
          options={[
            { value: "active", label: t("admin.sa.filter.active") },
            { value: "disabled", label: t("admin.sa.filter.disabled") },
            { value: "all", label: t("admin.sa.filter.all") },
            { value: "removed", label: t("admin.sa.filter.removed") },
          ]}
        />
        {status !== "removed" ? (
          <Button onClick={openCreate}>{t("admin.sa.create.open")}</Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(a) => a.id}
        empty={<span className="fawkes-portal__muted">{t("admin.sa.empty")}</span>}
      />
      {listActionError ? (
        <p className="fawkes-portal__error" role="alert">
          {listActionError}
        </p>
      ) : null}

      {/* Create */}
      <Modal
        open={createOpen}
        title={t("admin.sa.create.title")}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t("admin.common.cancel")}
            </Button>
            <Button onClick={submitCreate} disabled={mutating}>
              {mutating ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </>
        }
      >
        <p className="fawkes-portal__muted">{t("admin.sa.create.help")}</p>
        <Field
          label={t("admin.sa.column.username")}
          value={newUsername}
          onChange={(e) => setNewUsername(e.target.value)}
        />
        <p className="fawkes-portal__muted">{t("admin.sa.create.username.help")}</p>
        {createError ? (
          <p className="fawkes-portal__error" role="alert">
            {createError}
          </p>
        ) : null}
      </Modal>

      {/* Restore confirm */}
      <Modal
        open={confirm !== null}
        title={t("admin.sa.detail.danger")}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              {t("admin.common.cancel")}
            </Button>
            <Button
              disabled={mutating}
              onClick={() => {
                const action = confirm?.action;
                setConfirm(null);
                action?.();
              }}
            >
              {t("admin.sa.confirm.yes")}
            </Button>
          </>
        }
      >
        <p className="fawkes-portal__muted">{confirm?.message}</p>
      </Modal>
    </div>
  );
}

// ============================================================ detail (dedicated full-page view)

export function ServiceAccountDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { data, isLoading, isError, refetch } = useCustom<AdminServiceAccount>({
    url: `admin/service-accounts/${id}`,
    method: "get",
  });
  const { mutate, isLoading: mutating } = useCustomMutation();
  // Epic 9: live organizations, to bind a credential to the organization it acts for.
  const { data: orgsData } = useCustom<AdminOrganization[]>({ url: "admin/organizations", method: "get" });
  const organizations = orgsData?.data ?? [];
  const organizationOptions = [
    { value: "", label: t("admin.sa.cred.organization.none") },
    ...organizations.map((o) => ({ value: o.id, label: o.displayName || o.clientId })),
  ];
  const [mintOrganization, setMintOrganization] = useState("");
  const [bindChoice, setBindChoice] = useState<Record<string, string>>({});

  const [scopes, setScopes] = useState(DEFAULT_SCOPES);
  const [detailError, setDetailError] = useState<string | undefined>();
  const [issued, setIssued] = useState<IssuedCredential | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | undefined>();
  const [confirm, setConfirm] = useState<{ message: string; action: () => void } | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetLinkCopied, setResetLinkCopied] = useState(false);
  const [extendChoice, setExtendChoice] = useState<Record<string, string>>({});
  const [expireAllConfirm, setExpireAllConfirm] = useState(false);
  const [expireAllResult, setExpireAllResult] = useState<number | null>(null);

  if (isLoading) {
    return <LoadingState />;
  }
  const account = data?.data ?? null;
  if (isError || account === null) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  function reissueResetLink() {
    setDetailError(undefined);
    setResetLink(null);
    setResetLinkCopied(false);
    mutate(
      { url: `admin/service-accounts/${id}/reset-link`, method: "post", values: {} },
      {
        onSuccess: (res) => {
          const token = (res?.data as ResetLink | undefined)?.token;
          if (token) {
            setResetLink(`${window.location.origin}/set-password#${token}`);
          } else {
            setDetailError(t("admin.sa.detail.resetLink.error"));
          }
        },
        onError: () => setDetailError(t("admin.sa.detail.resetLink.error")),
      },
    );
  }

  async function copyResetLink() {
    if (!resetLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(resetLink);
    } catch {
      setDetailError(t("admin.sa.detail.resetLink.error"));
      return;
    }
    setResetLink(null);
    setResetLinkCopied(true);
  }

  function mintCredential() {
    setDetailError(undefined);
    setIssued(null);
    setSecretCopied(false);
    setCopyError(undefined);
    if (scopes.trim().length === 0) {
      setDetailError(t("admin.sa.detail.mint.error.scopes"));
      return;
    }
    mutate(
      {
        url: `admin/service-accounts/${id}/credentials`,
        method: "post",
        values: { scopes: scopes.trim(), organizationId: mintOrganization || null },
      },
      {
        onSuccess: (res) => {
          setScopes(DEFAULT_SCOPES);
          setMintOrganization("");
          setIssued((res?.data as IssuedCredential) ?? null);
          void refetch();
        },
        onError: (error) =>
          setDetailError(
            t(
              (error as { statusCode?: number } | undefined)?.statusCode === 400
                ? "admin.sa.detail.mint.error.unknownScopes"
                : "admin.sa.detail.mint.error.generic",
            ),
          ),
      },
    );
  }

  async function copySecret() {
    if (!issued) {
      return;
    }
    try {
      await navigator.clipboard.writeText(issued.plaintextSecret);
    } catch {
      setCopyError(t("admin.sa.detail.mint.copyError"));
      return;
    }
    setCopyError(undefined);
    setIssued((prev) => (prev ? { ...prev, plaintextSecret: "" } : null));
    setSecretCopied(true);
  }

  function credentialAction(cred: AdminCredential, verb: "delete" | "force-expire") {
    setDetailError(undefined);
    const url =
      verb === "delete"
        ? `admin/service-accounts/${id}/credentials/${cred.id}`
        : `admin/service-accounts/${id}/credentials/${cred.id}/force-expire`;
    mutate(
      { url, method: verb === "delete" ? "delete" : "post", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setDetailError(t("admin.sa.detail.action.error")),
      },
    );
  }

  function extendCredential(cred: AdminCredential) {
    setDetailError(undefined);
    const choice = extendChoice[cred.id] ?? EXTEND_DURATIONS[0].value;
    mutate(
      { url: `admin/service-accounts/${id}/credentials/${cred.id}/extend`, method: "post", values: { choice } },
      {
        onSuccess: () => void refetch(),
        onError: () => setDetailError(t("admin.sa.detail.action.error")),
      },
    );
  }

  function clearEndDate(cred: AdminCredential) {
    setDetailError(undefined);
    mutate(
      { url: `admin/service-accounts/${id}/credentials/${cred.id}/clear-end-date`, method: "post", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setDetailError(t("admin.sa.detail.action.error")),
      },
    );
  }

  function forceExpireAll() {
    setDetailError(undefined);
    mutate(
      { url: `admin/service-accounts/${id}/credentials/force-expire-all`, method: "post", values: {} },
      {
        onSuccess: (res) => {
          setExpireAllConfirm(false);
          setExpireAllResult((res?.data as { affected?: number } | undefined)?.affected ?? 0);
          void refetch();
        },
        onError: () => {
          setExpireAllConfirm(false);
          setDetailError(t("admin.sa.detail.action.error"));
        },
      },
    );
  }

  function toggleWebPortal() {
    setDetailError(undefined);
    const enabling = !account!.webPortalAccessEnabled;
    mutate(
      { url: `admin/service-accounts/${id}/web-portal-access`, method: enabling ? "put" : "delete", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setDetailError(t("admin.sa.detail.action.error")),
      },
    );
  }

  function toggleActive() {
    setDetailError(undefined);
    const enabling = !account!.active;
    mutate(
      { url: `admin/service-accounts/${id}/active`, method: enabling ? "put" : "delete", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setDetailError(t("admin.sa.detail.action.error")),
      },
    );
  }

  function softDelete() {
    setDetailError(undefined);
    mutate(
      { url: `admin/service-accounts/${id}`, method: "delete", values: {} },
      {
        onSuccess: () => onBack(),
        onError: () => setDetailError(t("admin.sa.detail.action.error")),
      },
    );
  }

  // Epic 9: (re)bind a credential; the choice "" unbinds. Applies from the credential's next token.
  function bindCredential(cred: AdminCredential) {
    setDetailError(undefined);
    const choice = bindChoice[cred.id] ?? cred.organizationId ?? "";
    mutate(
      {
        url: `admin/service-accounts/${id}/credentials/${cred.id}/organization`,
        method: "put",
        values: { organizationId: choice || null },
      },
      {
        onSuccess: () => void refetch(),
        onError: () => setDetailError(t("admin.sa.cred.organization.error")),
      },
    );
  }

  const credColumns: Column<AdminCredential>[] = [
    { header: t("admin.sa.cred.clientId"), cell: (c) => c.clientId },
    { header: t("admin.sa.cred.prefix"), cell: (c) => `${c.secretPrefix}…` },
    { header: t("admin.sa.cred.scopes"), cell: (c) => c.scopes || "—" },
    {
      header: t("admin.sa.cred.organization"),
      cell: (c) => (
        <span className="fawkes-admin__row-actions">
          {c.organizationId ? null : (
            <StatusBadge tone="warning">{t("admin.sa.cred.organization.unbound")}</StatusBadge>
          )}
          <SelectField
            label={t("admin.sa.cred.organization")}
            value={bindChoice[c.id] ?? c.organizationId ?? ""}
            onChange={(e) => setBindChoice((prev) => ({ ...prev, [c.id]: e.target.value }))}
            options={organizationOptions}
          />
          <Button
            variant="secondary"
            disabled={mutating || (bindChoice[c.id] ?? c.organizationId ?? "") === (c.organizationId ?? "")}
            onClick={() => bindCredential(c)}
          >
            {t("admin.sa.cred.organization.bind")}
          </Button>
        </span>
      ),
    },
    { header: t("admin.sa.cred.created"), cell: (c) => formatDateTime(c.createdAt, locale) },
    { header: t("admin.sa.cred.lastUsed"), cell: (c) => formatDateTime(c.lastUsedAt, locale) },
    { header: t("admin.sa.cred.expires"), cell: (c) => formatDateTime(c.expiresAt, locale) },
    {
      header: t("admin.common.actions"),
      align: "right",
      cell: (c) => (
        <span className="fawkes-admin__row-actions">
          <Button
            variant="secondary"
            disabled={mutating}
            onClick={() =>
              setConfirm({
                message: t("admin.sa.confirm.forceExpire"),
                action: () => credentialAction(c, "force-expire"),
              })
            }
          >
            {t("admin.sa.detail.forceExpire")}
          </Button>
          {/* Extend / clear only make sense for an expiring credential — the service no-ops on a
              no-expiry credential (extending would silently turn it expiring), so hide them. */}
          {c.expiresAt ? (
            <>
              <SelectField
                label={t("admin.sa.detail.extend.label")}
                value={extendChoice[c.id] ?? EXTEND_DURATIONS[0].value}
                onChange={(e) => setExtendChoice((prev) => ({ ...prev, [c.id]: e.target.value }))}
                options={EXTEND_DURATIONS.map((d) => ({ value: d.value, label: t(d.labelKey) }))}
              />
              <Button variant="secondary" disabled={mutating} onClick={() => extendCredential(c)}>
                {t("admin.sa.detail.extend.submit")}
              </Button>
              <Button
                variant="secondary"
                disabled={mutating}
                onClick={() =>
                  setConfirm({ message: t("admin.sa.confirm.clearEndDate"), action: () => clearEndDate(c) })
                }
              >
                {t("admin.sa.detail.clearEndDate")}
              </Button>
            </>
          ) : null}
          <Button variant="secondary" disabled={mutating} onClick={() => credentialAction(c, "delete")}>
            {t("admin.sa.detail.revoke")}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="fawkes-admin-page">
      <div className="fawkes-admin__toolbar">
        <Button variant="secondary" onClick={onBack}>
          {t("admin.sa.detail.back")}
        </Button>
      </div>

      <h2 className="fawkes-admin__detail-title">{account.username}</h2>

      <div className="fawkes-admin__detail">
        <ToggleField
          label={t("admin.sa.detail.active")}
          checked={account.active}
          disabled={mutating}
          onChange={() => toggleActive()}
        />

        <ToggleField
          label={t("admin.sa.detail.webPortal")}
          checked={account.webPortalAccessEnabled}
          disabled={mutating}
          onChange={() => toggleWebPortal()}
        />
        <p className="fawkes-portal__muted">{t("admin.sa.detail.webPortal.help")}</p>

        {/* Reissue reset link — only for web-portal (interactive-login) accounts. */}
        {account.webPortalAccessEnabled ? (
          <div className="fawkes-admin__reset-link">
            <Button
              variant="secondary"
              disabled={mutating}
              onClick={() =>
                setConfirm({ message: t("admin.sa.confirm.resetLink"), action: () => reissueResetLink() })
              }
            >
              {t("admin.sa.detail.resetLink.button")}
            </Button>
            {resetLink ? (
              <div className="fawkes-admin__secret" role="status">
                <strong>{t("admin.sa.detail.resetLink.revealTitle")}</strong>
                <p className="fawkes-portal__muted">{t("admin.sa.detail.resetLink.revealOnce")}</p>
                <code className="fawkes-admin__secret-value">{resetLink}</code>
                <Button variant="secondary" onClick={() => void copyResetLink()}>
                  {t("admin.sa.detail.resetLink.copy")}
                </Button>
              </div>
            ) : null}
            {resetLinkCopied ? (
              <p className="fawkes-portal__muted" role="status">
                {t("admin.sa.detail.resetLink.copied")}
              </p>
            ) : null}
          </div>
        ) : null}

        <h3 className="fawkes-admin__section">{t("admin.sa.detail.credentials")}</h3>
        <div className="fawkes-admin__mint">
          <Field
            label={t("admin.sa.detail.mint.scopes")}
            placeholder="foster.activate absences.read"
            value={scopes}
            onChange={(e) => setScopes(e.target.value)}
          />
          <SelectField
            label={t("admin.sa.detail.mint.organization")}
            value={mintOrganization}
            onChange={(e) => setMintOrganization(e.target.value)}
            options={organizationOptions}
          />
          <Button disabled={mutating} onClick={() => mintCredential()}>
            {t("admin.sa.detail.mint.submit")}
          </Button>
        </div>

        {issued ? (
          <div className="fawkes-admin__secret" role="status">
            <strong>{t("admin.sa.detail.mint.revealTitle")}</strong>
            <p className="fawkes-portal__muted">
              {t("admin.sa.cred.clientId")}: <code>{issued.clientId}</code>
            </p>
            {secretCopied ? (
              <p className="fawkes-portal__muted" role="status">
                {t("admin.sa.detail.mint.copied")}
              </p>
            ) : (
              <>
                <p className="fawkes-portal__muted">{t("admin.sa.detail.mint.revealOnce")}</p>
                <code className="fawkes-admin__secret-value">{issued.plaintextSecret}</code>
                <Button variant="secondary" onClick={() => void copySecret()}>
                  {t("admin.sa.detail.mint.copy")}
                </Button>
                {copyError ? (
                  <p className="fawkes-portal__error" role="alert">
                    {copyError}
                  </p>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        <DataTable
          columns={credColumns}
          rows={account.credentials}
          getRowKey={(c) => c.id}
          empty={<span className="fawkes-portal__muted">{t("admin.sa.detail.noCredentials")}</span>}
        />

        <RevokedCredentialsPanel userId={account.id} locale={locale} onRestored={() => void refetch()} />

        {/* Account-wide kill-switch — force-expire EVERY live credential (incident response). */}
        <div className="fawkes-admin__expire-all">
          {expireAllConfirm ? (
            <span className="fawkes-admin__row-actions">
              <span className="fawkes-portal__muted">{t("admin.sa.detail.expireAll.confirm")}</span>
              <Button variant="secondary" disabled={mutating} onClick={() => forceExpireAll()}>
                {t("admin.sa.detail.expireAll.confirmYes")}
              </Button>
              <Button variant="secondary" disabled={mutating} onClick={() => setExpireAllConfirm(false)}>
                {t("admin.common.cancel")}
              </Button>
            </span>
          ) : (
            <Button
              variant="secondary"
              disabled={mutating}
              onClick={() => {
                setExpireAllResult(null);
                setExpireAllConfirm(true);
              }}
            >
              {t("admin.sa.detail.expireAll.submit")}
            </Button>
          )}
          {expireAllResult !== null ? (
            <p className="fawkes-portal__muted" role="status">
              {t("admin.sa.detail.expireAll.result")} {expireAllResult}
            </p>
          ) : null}
        </div>

        <AllowlistPanel userId={account.id} locale={locale} />

        <h3 className="fawkes-admin__section">{t("admin.sa.detail.danger")}</h3>
        <Button
          variant="secondary"
          disabled={mutating}
          onClick={() =>
            setConfirm({
              // i18nProvider has no param interpolation — substitute {username} here.
              message: t("admin.sa.confirm.softDelete").replace("{username}", account.username),
              action: () => softDelete(),
            })
          }
        >
          {t("admin.sa.detail.softDelete")}
        </Button>

        {detailError ? (
          <p className="fawkes-portal__error" role="alert">
            {detailError}
          </p>
        ) : null}
      </div>

      {/* Shared confirm — every destructive/non-destructive lifecycle action routes through here. */}
      <Modal
        open={confirm !== null}
        title={t("admin.sa.detail.danger")}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              {t("admin.common.cancel")}
            </Button>
            <Button
              disabled={mutating}
              onClick={() => {
                const action = confirm?.action;
                setConfirm(null);
                action?.();
              }}
            >
              {t("admin.sa.confirm.yes")}
            </Button>
          </>
        }
      >
        <p className="fawkes-portal__muted">{confirm?.message}</p>
      </Modal>
    </div>
  );
}

// jmix admin-gap: the per-account IP allowlist (legacy ServiceAccountDetailView "Allowed IPs" grid).
// Its own GET fetches the entries WITH ids (the list/detail DTO only carries bare CIDR strings), so
// remove-by-id works; add/remove refetch this list. A brand-new account has zero IPs → the runbook's
// warning copy (callers are denied once the per-organization allowlist is enabled). 400 (invalid CIDR) and
// 409 (duplicate) surface inline; 401/403 → logout via authProvider.onError.
function AllowlistPanel({ userId, locale }: { userId: string; locale: Locale }) {
  const t = useTranslate();
  const { data, isLoading, refetch } = useCustom<AllowedIp[]>({
    url: `admin/service-accounts/${userId}/allowed-ips`,
    method: "get",
  });
  const { mutate, isLoading: mutating } = useCustomMutation();
  const [newCidr, setNewCidr] = useState("");
  const [error, setError] = useState<string | undefined>();

  const rows = data?.data ?? [];

  function addCidr() {
    setError(undefined);
    const cidr = newCidr.trim();
    if (cidr.length === 0) {
      setError(t("admin.sa.allowlist.error.required"));
      return;
    }
    mutate(
      { url: `admin/service-accounts/${userId}/allowed-ips`, method: "post", values: { cidr } },
      {
        onSuccess: () => {
          setNewCidr("");
          void refetch();
        },
        onError: (err) => {
          const s = statusOf(err);
          setError(
            s === 409
              ? t("admin.sa.allowlist.error.duplicate")
              : s === 400
                ? t("admin.sa.allowlist.error.invalid")
                : t("admin.sa.allowlist.error.generic"),
          );
        },
      },
    );
  }

  function removeCidr(ipId: string) {
    setError(undefined);
    mutate(
      { url: `admin/service-accounts/${userId}/allowed-ips/${ipId}`, method: "delete", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setError(t("admin.sa.allowlist.error.generic")),
      },
    );
  }

  return (
    <>
      <h3 className="fawkes-admin__section">{t("admin.sa.detail.allowlist")}</h3>
      {!isLoading && rows.length === 0 ? (
        <p className="fawkes-portal__banner" role="alert">
          {t("admin.sa.allowlist.zeroWarning")}
        </p>
      ) : null}
      <div className="fawkes-admin__mint">
        <Field
          label={t("admin.sa.allowlist.add.label")}
          placeholder="10.0.0.0/8"
          value={newCidr}
          onChange={(e) => setNewCidr(e.target.value)}
        />
        <Button disabled={mutating} onClick={addCidr}>
          {t("admin.sa.allowlist.add.submit")}
        </Button>
      </div>
      {rows.length > 0 ? (
        <ul className="fawkes-portal__list">
          {rows.map((ip) => (
            <li key={ip.id} className="fawkes-portal__row">
              <code>{ip.cidr}</code>
              <span className="fawkes-portal__muted">
                {t("admin.sa.allowlist.added")}: {formatDateTime(ip.createdAt, locale)}
              </span>
              <Button variant="secondary" disabled={mutating} onClick={() => removeCidr(ip.id)}>
                {t("admin.sa.allowlist.remove")}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <p className="fawkes-portal__error" role="alert">
          {error}
        </p>
      ) : null}
    </>
  );
}

// R3 (visual-parity follow-up): revoked-credential restore. A revoked credential is soft-deleted and
// drops out of the live grid; this panel lazily fetches the account's removed credentials (only when the
// operator toggles it on) and offers per-row restore. Restoring refetches both this panel and the parent
// detail (so the credential reappears in the live grid). Owner must be live — the endpoint 404s otherwise.
function RevokedCredentialsPanel({
  userId,
  locale,
  onRestored,
}: {
  userId: string;
  locale: Locale;
  onRestored: () => void;
}) {
  const t = useTranslate();
  const [show, setShow] = useState(false);
  const { data, isLoading, refetch } = useCustom<AdminCredential[]>({
    url: `admin/service-accounts/${userId}/credentials?status=removed`,
    method: "get",
    queryOptions: { enabled: show },
  });
  const { mutate, isLoading: mutating } = useCustomMutation();
  const [error, setError] = useState<string | undefined>();
  const [pendingRestore, setPendingRestore] = useState<string | null>(null);

  const rows = data?.data ?? [];

  function restore(cred: AdminCredential) {
    setError(undefined);
    mutate(
      { url: `admin/service-accounts/${userId}/credentials/${cred.id}/restore`, method: "post", values: {} },
      {
        onSuccess: () => {
          void refetch();
          onRestored();
        },
        onError: () => setError(t("admin.sa.detail.action.error")),
      },
    );
  }

  return (
    <div className="fawkes-admin__revoked">
      <ToggleField
        label={t("admin.sa.detail.showRevoked")}
        checked={show}
        onChange={() => setShow((v) => !v)}
      />
      {show ? (
        !isLoading && rows.length === 0 ? (
          <p className="fawkes-portal__muted">{t("admin.sa.detail.noRevoked")}</p>
        ) : (
          <ul className="fawkes-portal__list">
            {rows.map((c) => (
              <li key={c.id} className="fawkes-portal__row">
                <code>{c.clientId}</code>
                <span className="fawkes-portal__muted">
                  {c.scopes || "—"} · {formatDateTime(c.createdAt, locale)}
                </span>
                {pendingRestore === c.id ? (
                  <span className="fawkes-admin__row-actions">
                    <span className="fawkes-portal__muted">{t("admin.sa.confirm.restoreCredential")}</span>
                    <Button
                      variant="secondary"
                      disabled={mutating}
                      onClick={() => {
                        setPendingRestore(null);
                        restore(c);
                      }}
                    >
                      {t("admin.sa.confirm.yes")}
                    </Button>
                    <Button variant="secondary" disabled={mutating} onClick={() => setPendingRestore(null)}>
                      {t("admin.common.cancel")}
                    </Button>
                  </span>
                ) : (
                  <Button variant="secondary" disabled={mutating} onClick={() => setPendingRestore(c.id)}>
                    {t("admin.sa.detail.restoreCredential")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )
      ) : null}
      {error ? (
        <p className="fawkes-portal__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
